/**
 * assets/fitment-badge.js
 *
 * Fitment badge controller for BOTH:
 *   - Product page badge:  [data-fitment-badge]          (full banner, all 4 states)
 *   - Product card badge:  [data-fitment-card-badge]     (compact pill, positive-only)
 *
 * CARD BADGE POLICY: cards only render POSITIVE signals ("Fits", "Universal").
 * When the active vehicle is NOT compatible and the product is not universal,
 * the card badge stays silently hidden. Rationale: a wall of amber on a
 * collection page is demoralising and makes the store feel hostile; the full
 * product-page banner still shows "Doesn't fit" where it matters for the
 * purchase decision.
 *
 * FULL BANNER POLICY (unchanged): shows all 4 states, including amber "Doesn't
 * fit" and the grey "Add your car to check fitment" prompt when no vehicle is
 * active.
 *
 * - Reads product fitment data from data-attributes on each badge element.
 * - Compares against the active garage vehicle.
 * - Re-evaluates live whenever the garage changes (add / remove / setActive / clear).
 * - The full badge's "Add your car" prompt opens the garage modal.
 *
 * ID NORMALISATION:
 *   Garage vehicles store full GIDs, e.g. "gid://shopify/Metaobject/175080767781".
 *   Liquid's v.system.id on a metaobject reference returns just the numeric part,
 *   e.g. "175080767781". We normalise both sides to the numeric tail before comparing.
 *
 * Enable debug logs in the browser console with:
 *   window.ChicaneFitmentDebug = true;
 */
(function () {
  'use strict';

  var EVENT_CHANGED       = 'chicane:garage:changed';
  var FULL_BADGE_SELECTOR = '[data-fitment-badge]';
  var CARD_BADGE_SELECTOR = '[data-fitment-card-badge]';
  var INIT_POLL_INTERVAL  = 250;   // ms
  var INIT_POLL_TIMEOUT   = 8000;  // ms

  function log() {
    if (window.ChicaneFitmentDebug && window.console && console.log) {
      console.log.apply(console, ['[fitment-badge]'].concat([].slice.call(arguments)));
    }
  }

  // ---- ID normalisation ----------------------------------------------------
  // Accepts any of: "gid://shopify/Metaobject/123", "Metaobject/123", "123", number.
  // Returns the numeric tail as a string, or '' if unparseable.
  function normaliseId(raw) {
    if (raw === null || raw === undefined) return '';
    var str = String(raw);
    var m = str.match(/(\d+)\s*$/);
    return m ? m[1] : '';
  }

  // ---- Garage accessors ----------------------------------------------------
  function getGarage() {
    return (window.ChicaneFitment && window.ChicaneFitment.Garage) || null;
  }

  function getActiveVehicle() {
    var g = getGarage();
    if (!g || typeof g.getActive !== 'function') return null;
    try { return g.getActive(); } catch (e) { return null; }
  }

  function getVehicleId(v) {
    if (!v) return null;
    return v.id || v.gid || v.vehicleId || null;
  }

  function getVehicleDisplayName(v) {
    if (!v) return '';
    return v.displayName || v.display || v.name || v.title || '';
  }

  // ---- Shared helpers ------------------------------------------------------
  function parseIds(badge) {
    var raw = badge.getAttribute('data-fitment-vehicles') || '[]';
    try {
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      var out = [];
      for (var i = 0; i < parsed.length; i++) {
        var n = normaliseId(parsed[i]);
        if (n) out.push(n);
      }
      return out;
    } catch (e) {
      log('Could not parse data-fitment-vehicles:', raw);
      return [];
    }
  }

  function hideAllStates(badge) {
    var states = badge.querySelectorAll('[data-state]');
    for (var i = 0; i < states.length; i++) {
      states[i].hidden = true;
    }
  }

  function showState(badge, stateName, vehicleName) {
    hideAllStates(badge);
    var el = badge.querySelector('[data-state="' + stateName + '"]');
    if (!el) return;
    if (vehicleName) {
      var nameEl = el.querySelector('[data-fitment-vehicle-name]');
      if (nameEl) nameEl.textContent = vehicleName;
    }
    el.hidden = false;
    badge.hidden = false;
  }

  // ---- Full product-page badge ---------------------------------------------
  // Supports all 4 states including prompt (no active vehicle) and no-fit.
  function evaluateFullBadge(badge) {
    var hasData     = badge.getAttribute('data-fitment-has-data') === 'true';
    var isUniversal = badge.getAttribute('data-fitment-universal') === 'true';
    var vehicleIds  = parseIds(badge);

    if (!hasData) {
      badge.hidden = true;
      return;
    }

    var active = getActiveVehicle();

    if (!active) {
      showState(badge, 'prompt');
      return;
    }

    var activeId   = normaliseId(getVehicleId(active));
    var activeName = getVehicleDisplayName(active);

    if (activeId && vehicleIds.indexOf(activeId) !== -1) {
      showState(badge, 'fits', activeName);
      return;
    }
    if (isUniversal) {
      showState(badge, 'universal');
      return;
    }
    showState(badge, 'no-fit', activeName);
  }

  // ---- Card badge ----------------------------------------------------------
  // POSITIVE SIGNALS ONLY: renders 'fits' or 'universal'. Hides entirely
  // when there is no active vehicle, the product is untagged, OR the
  // active vehicle is not a match (no amber pill on cards by design).
  function evaluateCardBadge(badge) {
    var hasData     = badge.getAttribute('data-fitment-has-data') === 'true';
    var isUniversal = badge.getAttribute('data-fitment-universal') === 'true';
    var vehicleIds  = parseIds(badge);

    if (!hasData) {
      badge.hidden = true;
      return;
    }

    var active = getActiveVehicle();
    if (!active) {
      badge.hidden = true;
      return;
    }

    var activeId = normaliseId(getVehicleId(active));

    if (activeId && vehicleIds.indexOf(activeId) !== -1) {
      showState(badge, 'fits');
      return;
    }
    if (isUniversal) {
      showState(badge, 'universal');
      return;
    }

    // Not a match and not universal → stay silent on cards.
    badge.hidden = true;
  }

  function evaluateAll() {
    var full = document.querySelectorAll(FULL_BADGE_SELECTOR);
    for (var i = 0; i < full.length; i++) evaluateFullBadge(full[i]);

    var cards = document.querySelectorAll(CARD_BADGE_SELECTOR);
    for (var j = 0; j < cards.length; j++) evaluateCardBadge(cards[j]);

    log('evaluated', full.length, 'full badges and', cards.length, 'card badges');
  }

  // ---- Garage event wiring -------------------------------------------------
  // Wrap garage mutation methods so we can fire a single change event
  // without having to modify fitment-garage.js.
  var wrapped = false;
  function wrapGarage() {
    if (wrapped) return;
    var g = getGarage();
    if (!g) return;

    var methods = ['addVehicle', 'removeVehicle', 'setActive', 'clear'];
    for (var i = 0; i < methods.length; i++) {
      var name = methods[i];
      var original = g[name];
      if (typeof original === 'function' && !original._chicaneBadgeWrapped) {
        g[name] = (function (methodName, fn) {
          var wrapper = function () {
            var result = fn.apply(this, arguments);
            try {
              document.dispatchEvent(new CustomEvent(EVENT_CHANGED, {
                detail: { method: methodName }
              }));
            } catch (e) {}
            return result;
          };
          wrapper._chicaneBadgeWrapped = true;
          return wrapper;
        })(name, original);
      }
    }
    wrapped = true;
    log('garage wrapped');
  }

  // ---- Prompt click → open garage modal ------------------------------------
  function openGarageModal() {
    var g = getGarage();

    // 1. Preferred: call the garage UI directly.
    if (g && g.UI) {
      if (typeof g.UI.open   === 'function') { g.UI.open();   return; }
      if (typeof g.UI.show   === 'function') { g.UI.show();   return; }
      if (typeof g.UI.toggle === 'function') { g.UI.toggle(); return; }
    }

    // 2. Fallback: click an existing garage trigger in the header.
    var headerBtn = document.querySelector(
      '[data-garage-trigger], [data-garage-button], [data-fitment-garage-open], #fitment-garage-trigger, .header__garage-button'
    );
    if (headerBtn) { headerBtn.click(); return; }

    // 3. Last resort: dispatch an event for any listener to handle.
    document.dispatchEvent(new CustomEvent('chicane:garage:open'));
    log('fell back to chicane:garage:open event');
  }

  function wirePromptClicks() {
    document.addEventListener('click', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var btn = target.closest('[data-fitment-open-garage]');
      if (!btn) return;
      e.preventDefault();
      openGarageModal();
    });
  }

  // ---- Init ----------------------------------------------------------------
  function init() {
    // First pass — may run before fitment-garage.js has attached; the poll below covers that.
    evaluateAll();
    wrapGarage();
    wirePromptClicks();

// Re-evaluate whenever the garage changes.
    document.addEventListener(EVENT_CHANGED, evaluateAll);

    // Also re-evaluate on demand (fitment-results.js fires this after
    // injecting new cards into the /pages/fitment grid via AJAX).
    document.addEventListener('chicane:badges:refresh', evaluateAll);

    // Poll briefly for the garage in case this script loaded first.
    var elapsed = 0;
    var poll = setInterval(function () {
      elapsed += INIT_POLL_INTERVAL;
      if (getGarage()) {
        wrapGarage();
        evaluateAll();
        clearInterval(poll);
        return;
      }
      if (elapsed >= INIT_POLL_TIMEOUT) {
        clearInterval(poll);
        log('gave up waiting for garage after', INIT_POLL_TIMEOUT, 'ms');
      }
    }, INIT_POLL_INTERVAL);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();